import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { fetchGoogleReviews, GOOGLE_REVIEWS_READ_URL } from "@/utils/googleReviews";

function GoogleG({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-shrink-0">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

/**
 * Lightweight Google-reviews trust pill for high-intent pages.
 * Consumes /api/google-reviews (server-side cached, 24h).
 *
 * @param {"card"|"inline"} variant  card = subtle bordered row (default); inline = flat, borderless.
 * @param {string} reassurance       Optional short reassurance line (e.g. "We reply in under 2 business hours").
 * @param {string} testId            Optional data-testid override.
 */
export default function GoogleTrustPill({ variant = "card", reassurance = "", testId = "google-trust-pill" }) {
  const [data, setData] = useState(null);
  useEffect(() => { fetchGoogleReviews().then(setData); }, []);

  const place = data?.place || null;
  const rating = place?.rating ? Number(place.rating).toFixed(1) : "4.8";
  const count = place?.reviewCount || 0;

  const shell =
    variant === "inline"
      ? "flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 py-2"
      : "flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 rounded-full bg-brand-aqua-light/40 border border-brand-light-grey px-4 py-2.5";

  return (
    <a
      href={GOOGLE_REVIEWS_READ_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${shell} text-xs sm:text-sm group transition-colors hover:border-brand-aqua/40`}
      data-testid={testId}
      aria-label={`Rated ${rating} on Google${count ? ` from ${count} reviews` : ""}`}
    >
      <span className="flex items-center gap-2">
        <GoogleG size={14} />
        <span className="font-semibold text-brand-black">Google</span>
      </span>
      <span className="flex items-center gap-1" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} size={13} className="fill-amber-400 text-amber-400" />
        ))}
      </span>
      <span className="font-heading font-bold text-brand-black">{rating}</span>
      {count > 0 && <span className="text-brand-grey">{`· ${count} reviews`}</span>}
      {reassurance && (
        <>
          <span className="hidden sm:inline-block w-px h-3.5 bg-brand-light-grey" aria-hidden="true" />
          <span className="text-brand-grey group-hover:text-brand-black transition-colors">{reassurance}</span>
        </>
      )}
    </a>
  );
}
